import type {
  BasicMaterial,
  DeepPartial,
  ManifestNode,
  ManifestResponse,
  MaterialResponse,
} from "@/types";
import { DoubleSide, FrontSide } from "three";

const rawAmethystDiceNodeOverrides: DeepPartial<ManifestNode> = {
  materials: {
    dieMaterial: {
      properties: {
        albedoColor: "#482166",
        normalScale: [0.5, 0.5],
        emissiveIntensity: 0.5,
        clearCoat: {
          intensity: 0.25,
        },
        subSurface: {
          volumeIndexOfRefraction: 1.15,
        },
      },
    },
  },
};

const enlightenmentDiceNodeOverrides: DeepPartial<ManifestNode> = {
  materials: {
    phong1: {
      properties: {
        transparencyMode: 1,
      },
    },
  },
};

const legoTileNodeOverrides: DeepPartial<ManifestNode> = {
  materials: {
    numberMat: {
      properties: {
        // clone for uv offset animations
        albedoTextureClone: true,
      },
    },
  },
};

const spelljammerNumberAtlasNodeOverrides: DeepPartial<ManifestNode> = {
  materials: {
    numbersMat: {
      properties: {
        // clone for uv offset animations
        albedoTextureClone: true,
        opacityTextureClone: true,
        useThreeTransparency: true,
        transparent: true,
        opacity: 0.0,
        depthTest: false,
      },
    },
  },
};

const spelljammerDieAnimationOverrides: DeepPartial<ManifestNode> = {
  animations: [
    {},
    {
      definition: "dieScaleIn",
      triggerStart: "rollBegin",
      triggerStop: "dieRemoved",
      loop: false,
    },
  ],
};

const planarSummoningDieMaterialOverrides: DeepPartial<ManifestNode> = {
  materials: {
    default: {
      disableShadow: true,
      properties: {
        useThreeTransparency: true,
        transparent: true,
        alphaTest: 0.5,
        metallic: 0.0,
        roughness: 1.0,
      },
    },
    dXBaseMat: {
      properties: {
        useThreeTransparency: true,
        albedoTexture: "",
        albedoColor: "#777777",
        // move the rainbow texture from albedo to specularColorMap
        // to reduce the strong affect on the other materials
        specularColorMap: "rainbow",
        transparent: true,
        metallic: 0.0,
        // converting this to a transmission material to avoid transparency glitches
        subSurface: {
          refractionIntensity: 1.0,
          volumeIndexOfRefraction: 1.15,
          minimumThickness: 2,
          maximumThickness: 4,
        },
        depthTest: true,
        depthWrite: false,
      },
    },
    dXRevMat: {
      properties: {
        useThreeTransparency: true,
        side: FrontSide,
      },
    },
    NebulaMat: {
      properties: {
        useThreeTransparency: true,
        transparent: true,
        depthTest: true,
        depthWrite: false,
        side: DoubleSide,
      },
    },
  },
};

const fuzzyDieMaterialOverrides: DeepPartial<ManifestNode> = {
  materials: {
    base: {
      properties: {
        useThreeTransparency: true,
        transparent: false,
        depthWrite: true,
      },
    },
    layer1: {
      properties: {
        useThreeTransparency: true,
        transparent: true,
        depthWrite: false,
      },
    },
    layer2: {
      properties: {
        useThreeTransparency: true,
        transparent: true,
        depthWrite: false,
      },
    },
    layer3: {
      properties: {
        useThreeTransparency: true,
        transparent: true,
        opacity: 0.73,
        alphaTest: 0.4,
        depthWrite: false,
      },
    },
    layer4: {
      properties: {
        useThreeTransparency: true,
        transparent: true,
        opacity: 0.67,
        alphaTest: 0.4,
        depthWrite: false,
      },
    },
    layer5: {
      properties: {
        useThreeTransparency: true,
        transparent: true,
        opacity: 0.59,
        alphaTest: 0.4,
        depthWrite: false,
      },
    },
  },
};

const tharizdunDieMaterialOverrides: DeepPartial<ManifestNode> = {
  materials: {
    MAT_EX: {
      properties: {
        indexOfRefraction: 2.0,
      },
    },
    MAT_Number: {
      properties: {
        useThreeTransparency: true,
        alphaTest: 0.4,
      },
    },
  },
};

const upsideDownDieMaterialOverrides: DeepPartial<ManifestNode> = {
  materials: {
    NumberMat: {
      properties: {
        emissiveIntensity: 0,
      },
    },
    FuzMat: {
      properties: {
        emissiveColor: "#1FA89A",
        emissiveTextureClone: true,
      },
    },
  },
};

// these are shared across the Tarrasque and Monster Manual dice sets
const translucentDieMaterialOverrides: DeepPartial<BasicMaterial> = {
  // ignore og transparency settings since we're using transmission
  useThreeTransparency: true,
  // converting this to a transmission material to best match the babylonjs subsurface translucency
  subSurface: {
    tintColor: "#ffffff",
    refractionIntensity: 1.0,
    volumeIndexOfRefraction: 1.0,
    minimumThickness: 0.01,
    maximumThickness: 0.01,
  },
};

const translucentSetDieMaterialOverrides: DeepPartial<ManifestResponse> = {
  d4: {
    materials: {
      Mat: {
        properties: {
          ...translucentDieMaterialOverrides,
          refractionTexture: "d4Normal",
        },
      },
    },
  },
  d6: {
    materials: {
      Mat: {
        properties: {
          ...translucentDieMaterialOverrides,
          refractionTexture: "d6Normal",
        },
      },
    },
  },
  d8: {
    materials: {
      Mat: {
        properties: {
          ...translucentDieMaterialOverrides,
          refractionTexture: "d8Normal",
        },
      },
    },
  },
  d10: {
    materials: {
      Mat: {
        properties: {
          ...translucentDieMaterialOverrides,
          refractionTexture: "d10Normal",
        },
      },
    },
  },
  d12: {
    materials: {
      Mat: {
        properties: {
          ...translucentDieMaterialOverrides,
          refractionTexture: "d12Normal",
        },
      },
    },
  },
  d20: {
    materials: {
      Mat: {
        properties: {
          ...translucentDieMaterialOverrides,
          refractionTexture: "d20Normal",
        },
      },
    },
  },
  d100: {
    materials: {
      Mat: {
        properties: {
          ...translucentDieMaterialOverrides,
          refractionTexture: "d00Normal",
        },
      },
    },
  },
};

const glacialDieMaterialOverrides: DeepPartial<ManifestNode> = {
  materials: {
    default: {
      properties: {
        alpha: 0.95,
      },
    },
  },
};

const themberchaudDieMaterialOverrides: DeepPartial<ManifestNode> = {
  materials: {
    baseMat: {
      properties: {
        marbleAmplitude: 0.0,
      },
    },
    numbersMat: {
      properties: {
        emissiveIntensity: 0.365,
      },
    },
  },
};

const infiniteDieAnimationOverrides: DeepPartial<ManifestNode> = {
  animations: [
    {},
    {
      definition: "dieScaleIn",
      triggerStart: "rollBegin",
      triggerStop: "dieRemoved",
      loop: false,
    },
  ],
};

const concordJewelDieMaterialOverrides: DeepPartial<BasicMaterial> = {
  emissiveIntensity: 0.9,
};

const dmgSkyllaDieMaterialOverrides: DeepPartial<ManifestNode> = {
  materials: {
    Mat: {
      properties: {
        emissiveIntensity: 0.365,
      },
    },
  },
};

const cauldronDieMaterialOverrides: DeepPartial<ManifestNode> = {
  materials: {
    default: {
      properties: {
        emissiveIntensity: 2.25,
      },
    },
  },
};

const clashOfThornsDieNumberMaterialOverrides: DeepPartial<ManifestNode> = {
  materials: {
    numberMaterial: {
      properties: {
        emissiveIntensity: 0.325,
      },
    },
  },
};

const dawnIncarnatesDieMaterialOverrides: DeepPartial<MaterialResponse> = {
  properties: {
    emissiveIntensity: 1.25,
    subSurface: {
      refractionIntensity: 0.9,
      minimumThickness: 0.02,
      maximumThickness: 0.04,
      volumeIndexOfRefraction: 2.0,
    },
  },
};

const elderFlameDieMaterialOverrides: DeepPartial<ManifestNode> = {
  materials: {
    numberCoreMat: {
      properties: {
        alpha: 0.25,
        emissiveIntensity: 0.5,
      },
    },
    woodMat: {
      properties: {
        indexOfRefraction: 1.0,
      },
    },
  },
};

const flourishingDieMaterialOverrides: DeepPartial<ManifestNode> = {
  materials: {
    default: {
      properties: {
        emissiveIntensity: 0.25,
      },
    },
  },
};

export const manifestOverrides: Record<
  string,
  DeepPartial<ManifestResponse>
> = {
  // Raw Amethyst
  "04701": {
    d4: rawAmethystDiceNodeOverrides,
    d6: rawAmethystDiceNodeOverrides,
    d8: rawAmethystDiceNodeOverrides,
    d10: rawAmethystDiceNodeOverrides,
    d12: rawAmethystDiceNodeOverrides,
    d20: rawAmethystDiceNodeOverrides,
    d100: rawAmethystDiceNodeOverrides,
  },
  // Dice of Enlightenment
  "02501": {
    d4: enlightenmentDiceNodeOverrides,
    d6: enlightenmentDiceNodeOverrides,
    d8: enlightenmentDiceNodeOverrides,
    d10: enlightenmentDiceNodeOverrides,
    d12: enlightenmentDiceNodeOverrides,
    d20: {
      ...enlightenmentDiceNodeOverrides,
      animations: [
        {
          definition: "crit",
          triggerStart: "maxRoll",
          // Stop this on dieRemoved so it doesn't mess with the dieRemoved animation scaling
          triggerStop: "dieRemoved",
          loop: false,
        },
        {},
      ],
    },
    d100: enlightenmentDiceNodeOverrides,
  },
  // Elder Heart
  "02101": {
    d20: {
      animations: [
        {
          definition: "animation_0",
          triggerStart: "maxRoll",
          // stopping the flowers here causes them to scale back to 0
          triggerStop: "",
          loop: false,
        },
      ],
    },
  },
  // Everlasting Rime
  "01201": {
    resources: {
      materials: {
        icicle: {
          properties: {
            emissiveColor: "#0a2252",
            emissiveTexture: "",
          },
        },
      },
      // @ts-expect-error: avoiding collision with resources and nodeResponse
      animationGroups: {
        icicles: {
          animationClones: {
            icicles14: {
              targetedAnimations: [
                {
                  targetId: "Clone of Icicles14.Icicles14",
                },
              ],
            },
            icicles15: {
              targetedAnimations: [
                {
                  targetId: "Clone of Icicles15.Icicles15",
                },
              ],
            },
          },
        },
      },
    },
    d4: {
      materials: {
        Die: {
          properties: {
            numberTexture: "d4_numbers",
            albedoTexture: "d4_mask",
          },
        },
      },
    },
    d6: {
      materials: {
        Die: {
          properties: {
            numberTexture: "d6_numbers",
            albedoTexture: "d6_mask",
          },
        },
      },
    },
    d8: {
      materials: {
        Die: {
          properties: {
            numberTexture: "d8_numbers",
            albedoTexture: "d8_mask",
          },
        },
      },
    },
    d10: {
      materials: {
        Die: {
          properties: {
            numberTexture: "d10_numbers",
            albedoTexture: "d10_mask",
          },
        },
      },
    },
    d12: {
      materials: {
        Die: {
          properties: {
            numberTexture: "d12_numbers",
            albedoTexture: "d12_mask",
          },
        },
      },
    },
    d20: {
      materials: {
        Die: {
          properties: {
            numberTexture: "d20_numbers",
            albedoTexture: "d20_mask",
          },
        },
      },
    },
    d100: {
      materials: {
        Die: {
          properties: {
            numberTexture: "d100_numbers",
            albedoTexture: "d10_mask",
          },
        },
      },
    },
  },
  // LEGO
  "05901": {
    d4Tile: legoTileNodeOverrides,
    d6Tile: legoTileNodeOverrides,
    d8Tile: legoTileNodeOverrides,
    d10Tile: legoTileNodeOverrides,
    d12Tile: legoTileNodeOverrides,
    d20Tile: legoTileNodeOverrides,
    d100Tile: legoTileNodeOverrides,
  },
  // Planar Summoning
  "03001": {
    d4: {
      materials: {
        default: planarSummoningDieMaterialOverrides.materials!.default,
        d4BaseMat: planarSummoningDieMaterialOverrides.materials!.dXBaseMat,
        d4RevMat: planarSummoningDieMaterialOverrides.materials!.dXRevMat,
        NebulaMat: planarSummoningDieMaterialOverrides.materials!.NebulaMat,
      },
    },
    d6: {
      materials: {
        default: planarSummoningDieMaterialOverrides.materials!.default,
        d6BaseMat: planarSummoningDieMaterialOverrides.materials!.dXBaseMat,
        d6RevMat: planarSummoningDieMaterialOverrides.materials!.dXRevMat,
        NebulaMat: planarSummoningDieMaterialOverrides.materials!.NebulaMat,
      },
    },
    d8: {
      materials: {
        default: planarSummoningDieMaterialOverrides.materials!.default,
        d8BaseMat: planarSummoningDieMaterialOverrides.materials!.dXBaseMat,
        d8RevMat: planarSummoningDieMaterialOverrides.materials!.dXRevMat,
        NebulaMat: planarSummoningDieMaterialOverrides.materials!.NebulaMat,
      },
    },
    d10: {
      materials: {
        default: planarSummoningDieMaterialOverrides.materials!.default,
        d10BaseMat: planarSummoningDieMaterialOverrides.materials!.dXBaseMat,
        d10RevMat: planarSummoningDieMaterialOverrides.materials!.dXRevMat,
        NebulaMat: planarSummoningDieMaterialOverrides.materials!.NebulaMat,
      },
    },
    d12: {
      materials: {
        default: planarSummoningDieMaterialOverrides.materials!.default,
        d12BaseMat: planarSummoningDieMaterialOverrides.materials!.dXBaseMat,
        d12RevMat: planarSummoningDieMaterialOverrides.materials!.dXRevMat,
        NebulaMat: planarSummoningDieMaterialOverrides.materials!.NebulaMat,
      },
    },
    d20: {
      materials: {
        default: planarSummoningDieMaterialOverrides.materials!.default,
        d20BaseMat: planarSummoningDieMaterialOverrides.materials!.dXBaseMat,
        d20RevMat: planarSummoningDieMaterialOverrides.materials!.dXRevMat,
        NebulaMat: planarSummoningDieMaterialOverrides.materials!.NebulaMat,
      },
    },
    d100: {
      materials: {
        default: planarSummoningDieMaterialOverrides.materials!.default,
        d10BaseMat: planarSummoningDieMaterialOverrides.materials!.dXBaseMat,
        d10RevMat: planarSummoningDieMaterialOverrides.materials!.dXRevMat,
        NebulaMat: planarSummoningDieMaterialOverrides.materials!.NebulaMat,
      },
    },
  },
  // Spelljammer
  "03801": {
    d4: spelljammerDieAnimationOverrides,
    d6: spelljammerDieAnimationOverrides,
    d8: spelljammerDieAnimationOverrides,
    d10: spelljammerDieAnimationOverrides,
    d12: spelljammerDieAnimationOverrides,
    d20: spelljammerDieAnimationOverrides,
    d100: spelljammerDieAnimationOverrides,
    numbersPlaneD4: spelljammerNumberAtlasNodeOverrides,
    numbersPlaneD6: spelljammerNumberAtlasNodeOverrides,
    numbersPlaneD8: spelljammerNumberAtlasNodeOverrides,
    numbersPlaneD10: spelljammerNumberAtlasNodeOverrides,
    numbersPlaneD12: spelljammerNumberAtlasNodeOverrides,
    numbersPlaneD20: spelljammerNumberAtlasNodeOverrides,
    numbersPlaneD100: spelljammerNumberAtlasNodeOverrides,
    numbersPlaneMaxRoll: spelljammerNumberAtlasNodeOverrides,
  },
  // Fuzzy
  "02401": {
    d4: {
      materials: {
        "Base.d4": fuzzyDieMaterialOverrides.materials!.base,
        "L1.d4": fuzzyDieMaterialOverrides.materials!.layer1,
        "L2.d4": fuzzyDieMaterialOverrides.materials!.layer2,
        "L3.d4": fuzzyDieMaterialOverrides.materials!.layer3,
        "L4.d4": fuzzyDieMaterialOverrides.materials!.layer4,
        "L5.d4": fuzzyDieMaterialOverrides.materials!.layer5,
      },
    },
    d6: {
      materials: {
        "Base.d6": fuzzyDieMaterialOverrides.materials!.base,
        "L1.d6": fuzzyDieMaterialOverrides.materials!.layer1,
        "L2.d6": fuzzyDieMaterialOverrides.materials!.layer2,
        "L3.d6": fuzzyDieMaterialOverrides.materials!.layer3,
        "L4.d6": fuzzyDieMaterialOverrides.materials!.layer4,
        "L5.d6": fuzzyDieMaterialOverrides.materials!.layer5,
      },
    },
    d8: {
      materials: {
        "Base.d8": fuzzyDieMaterialOverrides.materials!.base,
        "L1.d8": fuzzyDieMaterialOverrides.materials!.layer1,
        "L2.d8": fuzzyDieMaterialOverrides.materials!.layer2,
        "L3.d8": fuzzyDieMaterialOverrides.materials!.layer3,
        "L4.d8": fuzzyDieMaterialOverrides.materials!.layer4,
        "L5.d8": fuzzyDieMaterialOverrides.materials!.layer5,
      },
    },
    d10: {
      materials: {
        "Base.d10": fuzzyDieMaterialOverrides.materials!.base,
        "L1.d10": fuzzyDieMaterialOverrides.materials!.layer1,
        "L2.d10": fuzzyDieMaterialOverrides.materials!.layer2,
        "L3.d10": fuzzyDieMaterialOverrides.materials!.layer3,
        "L4.d10": fuzzyDieMaterialOverrides.materials!.layer4,
        "L5.d10": fuzzyDieMaterialOverrides.materials!.layer5,
      },
    },
    d12: {
      materials: {
        "Base.d12": fuzzyDieMaterialOverrides.materials!.base,
        "L1.d12": fuzzyDieMaterialOverrides.materials!.layer1,
        "L2.d12": fuzzyDieMaterialOverrides.materials!.layer2,
        "L3.d12": fuzzyDieMaterialOverrides.materials!.layer3,
        "L4.d12": fuzzyDieMaterialOverrides.materials!.layer4,
        "L5.d12": fuzzyDieMaterialOverrides.materials!.layer5,
      },
    },
    d20: {
      materials: {
        "Base.d20": fuzzyDieMaterialOverrides.materials!.base,
        "L1.d20": fuzzyDieMaterialOverrides.materials!.layer1,
        "L2.d20": fuzzyDieMaterialOverrides.materials!.layer2,
        "L3.d20": fuzzyDieMaterialOverrides.materials!.layer3,
        "L4.d20": fuzzyDieMaterialOverrides.materials!.layer4,
        "L5.d20": fuzzyDieMaterialOverrides.materials!.layer5,
      },
    },
    d100: {
      materials: {
        "Base.d00": fuzzyDieMaterialOverrides.materials!.base,
        "L1.d00": fuzzyDieMaterialOverrides.materials!.layer1,
        "L2.d00": fuzzyDieMaterialOverrides.materials!.layer2,
        "L3.d00": fuzzyDieMaterialOverrides.materials!.layer3,
        "L4.d00": fuzzyDieMaterialOverrides.materials!.layer4,
        "L5.d00": fuzzyDieMaterialOverrides.materials!.layer5,
      },
    },
  },
  // Legends of Greyhawk: Tharizdun
  "07801": {
    d4: tharizdunDieMaterialOverrides,
    d6: tharizdunDieMaterialOverrides,
    d8: tharizdunDieMaterialOverrides,
    d10: tharizdunDieMaterialOverrides,
    d12: tharizdunDieMaterialOverrides,
    d20: tharizdunDieMaterialOverrides,
    d100: tharizdunDieMaterialOverrides,
  },
  // Tarrasque
  "06801": translucentSetDieMaterialOverrides,
  // Monster Manual Beholder
  "06701": translucentSetDieMaterialOverrides,
  // Monster Manual Displacer Beast
  "06702": translucentSetDieMaterialOverrides,
  // Monster Manual Mimic
  "06703": translucentSetDieMaterialOverrides,
  // Monster Manual Mind Flayer
  "06704": translucentSetDieMaterialOverrides,
  // Glacial
  "00701": {
    d4: glacialDieMaterialOverrides,
    d6: glacialDieMaterialOverrides,
    d8: glacialDieMaterialOverrides,
    d10: glacialDieMaterialOverrides,
    d12: glacialDieMaterialOverrides,
    d20: glacialDieMaterialOverrides,
    d100: glacialDieMaterialOverrides,
  },
  // Clash of Thorns
  "04101": {
    d4: clashOfThornsDieNumberMaterialOverrides,
    d6: clashOfThornsDieNumberMaterialOverrides,
    d8: clashOfThornsDieNumberMaterialOverrides,
    d10: clashOfThornsDieNumberMaterialOverrides,
    d12: clashOfThornsDieNumberMaterialOverrides,
    d20: {
      materials: {
        HelmetMaterial: {
          properties: {
            transparencyMode: 1,
          },
        },
        numberMaterial: {
          properties: {
            emissiveIntensity: 0.325,
          },
        },
      },
    },
    d100: clashOfThornsDieNumberMaterialOverrides,
  },
  // Upside Down
  "07501": {
    d4: upsideDownDieMaterialOverrides,
    d6: upsideDownDieMaterialOverrides,
    d8: upsideDownDieMaterialOverrides,
    d10: upsideDownDieMaterialOverrides,
    d12: upsideDownDieMaterialOverrides,
    d20: upsideDownDieMaterialOverrides,
    d100: upsideDownDieMaterialOverrides,
  },
  // Flourishing
  "03601": {
    d4: flourishingDieMaterialOverrides,
    d6: flourishingDieMaterialOverrides,
    d8: flourishingDieMaterialOverrides,
    d10: flourishingDieMaterialOverrides,
    d12: flourishingDieMaterialOverrides,
    d20: {
      materials: {
        default: {
          properties: {
            // Temporarily disable upside value highlight on the d20 since it's on the wrong number
            useNumbers: false,
            emissiveIntensity: 0.25,
          },
        },
      },
      animations: [
        {},
        {},
        {},
        {},
        {
          // Stop the d20 removal animation which scales the rings again from 1 to 0
          definition: "d20Removal",
          triggerStart: "",
          loop: false,
        },
      ],
    },
    d100: flourishingDieMaterialOverrides,
  },
  // Themberchaud
  // The marbling seems like a copy pasta in the manifest - visually there is no marbling on baseMat
  // so just disabling it here vs adding a hack to the updateShader code
  "04601": {
    d4: themberchaudDieMaterialOverrides,
    d6: themberchaudDieMaterialOverrides,
    d8: themberchaudDieMaterialOverrides,
    d10: themberchaudDieMaterialOverrides,
    d12: themberchaudDieMaterialOverrides,
    d20: themberchaudDieMaterialOverrides,
    d100: themberchaudDieMaterialOverrides,
  },
  // Infinite
  "06101": {
    d4: infiniteDieAnimationOverrides,
    d6: infiniteDieAnimationOverrides,
    d8: infiniteDieAnimationOverrides,
    d10: infiniteDieAnimationOverrides,
    d12: infiniteDieAnimationOverrides,
    d20: infiniteDieAnimationOverrides,
    d100: infiniteDieAnimationOverrides,
  },
  // Concord Jewel
  "03301": {
    d4: {
      materials: {
        "D4.001": {
          properties: concordJewelDieMaterialOverrides,
        },
      },
    },
    d6: {
      materials: {
        "d6.001": {
          properties: concordJewelDieMaterialOverrides,
        },
      },
    },
    d8: {
      materials: {
        "D8.001": {
          properties: concordJewelDieMaterialOverrides,
        },
      },
    },
    d10: {
      materials: {
        d10: {
          properties: concordJewelDieMaterialOverrides,
        },
      },
    },
    d12: {
      materials: {
        d12: {
          properties: concordJewelDieMaterialOverrides,
        },
      },
    },
    d20: {
      materials: {
        d20: {
          properties: concordJewelDieMaterialOverrides,
        },
      },
    },
    d100: {
      materials: {
        d100: {
          properties: concordJewelDieMaterialOverrides,
        },
      },
    },
  },
  // Cauldron
  "01101": {
    d4: cauldronDieMaterialOverrides,
    d6: cauldronDieMaterialOverrides,
    d8: cauldronDieMaterialOverrides,
    d10: cauldronDieMaterialOverrides,
    d12: cauldronDieMaterialOverrides,
    d20: cauldronDieMaterialOverrides,
    d100: cauldronDieMaterialOverrides,
  },
  // DMG - Skylla
  "06502": {
    d4: dmgSkyllaDieMaterialOverrides,
    d6: dmgSkyllaDieMaterialOverrides,
    d8: dmgSkyllaDieMaterialOverrides,
    d10: dmgSkyllaDieMaterialOverrides,
    d12: dmgSkyllaDieMaterialOverrides,
    d20: dmgSkyllaDieMaterialOverrides,
    d100: dmgSkyllaDieMaterialOverrides,
  },
  // Dawn Incarnates
  "03501": {
    d4: {
      materials: {
        d4: dawnIncarnatesDieMaterialOverrides,
      },
    },
    d6: {
      materials: {
        d6: dawnIncarnatesDieMaterialOverrides,
      },
    },
    d8: {
      materials: {
        d8: dawnIncarnatesDieMaterialOverrides,
      },
    },
    d10: {
      materials: {
        d10: dawnIncarnatesDieMaterialOverrides,
      },
    },
    d12: {
      materials: {
        d12: dawnIncarnatesDieMaterialOverrides,
      },
    },
    d20: {
      materials: {
        d20: dawnIncarnatesDieMaterialOverrides,
      },
    },
    d100: {
      materials: {
        d100: dawnIncarnatesDieMaterialOverrides,
      },
    },
  },
  // Elder Flame
  "04901": {
    d4: elderFlameDieMaterialOverrides,
    d6: elderFlameDieMaterialOverrides,
    d8: elderFlameDieMaterialOverrides,
    d10: elderFlameDieMaterialOverrides,
    d12: elderFlameDieMaterialOverrides,
    d20: elderFlameDieMaterialOverrides,
    d100: elderFlameDieMaterialOverrides,
  },
  // Mythic
  "00401": {
    d20: {
      animations: [
        // Make sure idle stops also on dieRemoved so a non max roll fully scales down
        {
          definition: "Idle",
          triggerStart: "rollBegin",
          triggerStop: ["maxRoll", "dieRemoved"],
          loop: true,
        },
        // Stop this animation on dieRemoved which is what turns the d20 face around
        // so that the BlackHole animation shows correctly
        // Otherwise the d20 face rotates halfway between the two
        // We had to separately modify the 20 face quaternion in useAnimationEventHandler for a maxRoll
        {
          definition: "Cleanup",
          triggerStart: "maxRoll",
          triggerStop: "dieRemoved",
        },
        {},
        {},
      ],
    },
  },
};
